<!--
  ClientDepartmentsPanel.vue -- Client Portal v2 PR B.

  Build contract:
  docs/planning/2026-06-30-client-portal-v2-data-api-refactor.md section 5.1,
  decisions 1 / 5 / 6.

  Departments are CLIENT-level now (decision 5: the distributor configures
  them on behalf of the client). This panel is the distributor-app surface
  for:
    - department CRUD: create / list / rename / soft-disable (decision 6 --
      NEVER hard-delete; a disabled department's row persists so historical
      order snapshots stay readable)
    - client fiscal config: fiscal_year_start (drives budget period
      boundaries)
    - per-department budget config: a department-level cap + cadence
      (decision 1 -- DEPARTMENT-LEVEL ONLY)

  Store <-> department mapping lives on the store editor
  (StoreDepartmentMapping); this panel owns the departments themselves.

  Persistence via the @/services/api client (paths relative to /api),
  matching the sibling client panels.
-->
<template>
  <section class="cdp" data-testid="client-departments-panel">
    <header class="cdp__header">
      <h3 class="cdp__title">Departments &amp; Budgets</h3>
      <p class="cdp__sub">
        Departments are cost centers for this client's stores. Budgets cap a
        department's spend per fiscal period.
      </p>
    </header>

    <!-- Fiscal config -->
    <div class="cdp__fiscal" data-testid="client-fiscal-config">
      <v-text-field
        :model-value="fiscalYearStart"
        label="Fiscal year start (MM-DD)"
        placeholder="01-01"
        density="comfortable"
        variant="outlined"
        hide-details
        data-testid="client-fiscal-input"
        @update:modelValue="fiscalYearStart = $event"
      />
      <v-btn
        color="primary"
        rounded="pill"
        :loading="savingFiscal"
        data-testid="client-fiscal-save"
        @click="saveFiscalConfig"
      >
        Save fiscal year
      </v-btn>
    </div>

    <!-- Add-department form -->
    <div class="cdp__add" data-testid="client-dept-add">
      <v-text-field
        :model-value="newName"
        label="Department name"
        density="comfortable"
        variant="outlined"
        hide-details
        data-testid="client-dept-add-name"
        @update:modelValue="newName = $event"
      />
      <v-text-field
        :model-value="newCostCenter"
        label="Cost-center code"
        placeholder="Optional"
        density="comfortable"
        variant="outlined"
        hide-details
        data-testid="client-dept-add-cost-center"
        @update:modelValue="newCostCenter = $event"
      />
      <v-btn
        color="primary"
        rounded="pill"
        :loading="creating"
        data-testid="client-dept-add-submit"
        @click="createDepartment"
      >
        Add department
      </v-btn>
    </div>

    <div v-if="loading" data-testid="client-dept-loading">Loading...</div>

    <div
      v-else-if="departments.length === 0"
      class="cdp__empty"
      data-testid="client-dept-empty"
    >
      No departments yet.
    </div>

    <ul v-else class="cdp__list">
      <li
        v-for="dept in departments"
        :key="dept.id"
        class="cdp__row"
        :class="{ 'cdp__row--inactive': dept.is_active === false }"
        :data-testid="`client-dept-row-${dept.id}`"
      >
        <div class="cdp__row-main">
          <span class="cdp__row-name">{{ dept.name }}</span>
          <span v-if="dept.cost_center_code" class="cdp__row-cc">
            {{ dept.cost_center_code }}
          </span>
          <span
            v-if="dept.is_active === false"
            class="cdp__row-disabled"
            :data-testid="`client-dept-disabled-${dept.id}`"
          >
            Disabled
          </span>
        </div>

        <div class="cdp__row-actions">
          <v-btn
            v-if="dept.is_active !== false"
            size="small"
            variant="text"
            :data-testid="`client-dept-disable-${dept.id}`"
            @click="setActive(dept.id, false)"
          >
            Disable
          </v-btn>
          <v-btn
            v-else
            size="small"
            variant="text"
            :data-testid="`client-dept-enable-${dept.id}`"
            @click="setActive(dept.id, true)"
          >
            Enable
          </v-btn>
        </div>

        <!-- Per-department budget config -->
        <div class="cdp__budget" :data-testid="`client-dept-budget-${dept.id}`">
          <v-text-field
            prefix="$"
            inputmode="decimal"
            :model-value="budgetDraft[dept.id] ? budgetDraft[dept.id].cap_amount : ''"
            label="Budget cap (per period)"
            density="comfortable"
            variant="outlined"
            hide-details
            :data-testid="`client-dept-budget-cap-${dept.id}`"
            @update:modelValue="setBudgetField(dept.id, 'cap_amount', $event)"
          />
          <v-select
            :model-value="budgetDraft[dept.id] ? budgetDraft[dept.id].cadence : 'monthly'"
            :items="budgetCadences"
            label="Cadence"
            density="comfortable"
            variant="outlined"
            hide-details
            :data-testid="`client-dept-budget-cadence-${dept.id}`"
            @update:modelValue="setBudgetField(dept.id, 'cadence', $event)"
          />
          <v-btn
            color="primary"
            size="small"
            rounded="pill"
            :loading="!!savingBudget[dept.id]"
            :disabled="!!savingBudget[dept.id] || !!budgetLoadFailed[dept.id]"
            :data-testid="`client-dept-budget-save-${dept.id}`"
            @click="saveBudget(dept.id, budgetDraft[dept.id])"
          >
            Save budget
          </v-btn>
          <span
            v-if="budgetLoadFailed[dept.id]"
            class="cdp__budget-error"
            :data-testid="`client-dept-budget-error-${dept.id}`"
          >
            Could not load this budget. Reload before editing.
          </span>
        </div>

        <!--
          Per-department shipping address (Phase 1 of the shipping-address
          redesign). Each department carries its own structured, ShipEngine-
          validated ship-to used as a fallback when the store does not collect
          a shipping address at checkout.
        -->
        <div
          class="cdp__ship"
          :data-testid="`client-dept-shipping-${dept.id}`"
        >
          <ShippingAddressEditor
            :endpoint="`/clients/${clientId}/departments/${dept.id}/shipping-address`"
            label="Department shipping address"
            :disabled="!canWrite"
            :data-testid="`client-dept-shipping-editor-${dept.id}`"
          />
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import api from "@/services/api";
import ShippingAddressEditor from "./ShippingAddressEditor.vue";

export default {
  name: "ClientDepartmentsPanel",

  components: { ShippingAddressEditor },

  // Same capability injection ClientDetailPage uses. The department shipping-address
  // editor's Save writes through client.update; a client.read-only viewer must
  // see it disabled (otherwise Save 403s). Derive canWrite here rather than
  // relying on the parent's v-if so the disabled state is correct regardless of
  // how the panel is mounted.
  inject: {
    distributorLayoutCapabilities: { default: () => [] },
  },

  props: {
    clientId: { type: [Number, String], required: true },
  },

  data() {
    return {
      loading: true,
      creating: false,
      savingFiscal: false,
      departments: [],
      fiscalYearStart: "",
      newName: "",
      newCostCenter: "",
      budgetCadences: ["monthly", "quarterly", "annual"],
      // dept.id -> { cap_amount, cadence } draft.
      budgetDraft: {},
      // dept.id -> true while a budget save is in flight (double-submit guard).
      savingBudget: {},
      // dept.id -> true when the budget GET failed, so we don't let the user
      // save an empty-cap draft over a real budget.
      budgetLoadFailed: {},
    };
  },

  computed: {
    capabilities() {
      const inj = this.distributorLayoutCapabilities;
      if (Array.isArray(inj)) return inj;
      if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
      }
      return [];
    },
    canWrite() {
      return this.capabilities.includes("client.update");
    },
  },

  watch: {
    clientId() {
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const [deptsResp, clientResp] = await Promise.all([
          api.get(
            `/clients/${this.clientId}/departments/?include_inactive=true`,
          ),
          api.get(`/clients/${this.clientId}`),
        ]);
        this.departments =
          (deptsResp.data && deptsResp.data.departments) || [];
        const attrs =
          clientResp.data &&
          clientResp.data.data &&
          clientResp.data.data.attributes;
        this.fiscalYearStart = (attrs && attrs.fiscal_year_start) || "";
        // Seed an empty budget draft for each department.
        const drafts = {};
        for (const d of this.departments) {
          drafts[d.id] = { cap_amount: "", cadence: "monthly" };
        }
        this.budgetDraft = drafts;
        await this.loadBudgets();
      } catch (e) {
        this.departments = [];
      } finally {
        this.loading = false;
      }
    },

    async loadBudgets() {
      // Pull each department's current budget so the draft pre-fills.
      const failed = {};
      await Promise.all(
        this.departments.map(async (d) => {
          try {
            const resp = await api.get(
              `/clients/${this.clientId}/departments/${d.id}/budget`,
            );
            const budget = resp.data && resp.data.budget;
            if (budget) {
              this.budgetDraft[d.id] = {
                cap_amount: String(budget.cap_amount ?? ""),
                cadence: budget.cadence || "monthly",
              };
            }
          } catch (e) {
            // Mark this row's budget load as failed so the Save button is
            // disabled -- otherwise the empty draft would coerce to cap=0 and
            // a click would write $0 over a real budget the GET failed to
            // fetch (gate review P1).
            failed[d.id] = true;
          }
        }),
      );
      this.budgetLoadFailed = failed;
    },

    async createDepartment() {
      const name = (this.newName || "").trim();
      if (!name) {
        return;
      }
      this.creating = true;
      try {
        await api.post(`/clients/${this.clientId}/departments/`, {
          name,
          cost_center_code: (this.newCostCenter || "").trim() || null,
        });
        this.newName = "";
        this.newCostCenter = "";
        await this.load();
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Failed to create department")
          : null;
      } finally {
        this.creating = false;
      }
    },

    async setActive(deptId, isActive) {
      try {
        await api.patch(
          `/clients/${this.clientId}/departments/${deptId}`,
          { is_active: isActive },
        );
        await this.load();
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Failed to update department")
          : null;
      }
    },

    setBudgetField(deptId, field, value) {
      const draft = this.budgetDraft[deptId] || {
        cap_amount: "",
        cadence: "monthly",
      };
      this.budgetDraft = {
        ...this.budgetDraft,
        [deptId]: { ...draft, [field]: value },
      };
    },

    async saveBudget(deptId, draft) {
      if (!draft) {
        return;
      }
      // Double-submit guard (gate review P1): ignore re-entrant clicks.
      if (this.savingBudget[deptId]) {
        return;
      }
      // Don't let an empty draft from a failed budget load overwrite a real
      // budget with cap=0.
      if (this.budgetLoadFailed[deptId]) {
        return;
      }
      const cap = Number(draft.cap_amount);
      if (!Number.isFinite(cap) || cap < 0) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Budget cap must be >= 0")
          : null;
        return;
      }
      this.savingBudget = { ...this.savingBudget, [deptId]: true };
      try {
        await api.put(
          `/clients/${this.clientId}/departments/${deptId}/budget`,
          { cap_amount: cap, cadence: draft.cadence || "monthly" },
        );
        this.$emit("budget-saved", deptId);
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Failed to save budget")
          : null;
      } finally {
        const next = { ...this.savingBudget };
        delete next[deptId];
        this.savingBudget = next;
      }
    },

    async saveFiscalConfig() {
      this.savingFiscal = true;
      try {
        const value = (this.fiscalYearStart || "").trim() || null;
        const resp = await api.patch(
          `/clients/${this.clientId}/fiscal-config`,
          { fiscal_year_start: value },
        );
        this.fiscalYearStart =
          (resp.data && resp.data.fiscal_year_start) || "";
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Failed to save fiscal config")
          : null;
      } finally {
        this.savingFiscal = false;
      }
    },
  },
};
</script>

<style scoped>
.cdp {
  padding: 1rem 0;
}
.cdp__title {
  margin: 0;
}
.cdp__sub {
  color: var(--tf-fg-4);
  margin: 0.25rem 0 1rem 0;
}
.cdp__fiscal,
.cdp__add {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.cdp__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.cdp__row {
  border: 1px solid var(--tf-slate-divider);
  border-radius: var(--tf-radius-md);
  padding: 0.75rem;
}
.cdp__row--inactive {
  opacity: 0.6;
}
.cdp__row-main {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.cdp__row-name {
  font-weight: 600;
}
.cdp__row-cc {
  color: var(--tf-fg-4);
  font-size: 0.85em;
}
.cdp__row-disabled {
  color: var(--tf-warn, #b45309);
  font-size: 0.8em;
}
.cdp__budget {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}
.cdp__empty {
  color: var(--tf-fg-4);
}
</style>
